<script>
    import { page } from '$app/stores';
    import Button from './Button.svelte';
    import Modal from './Modal.svelte';
    import H2 from './H2.svelte';
    import { createEventDispatcher, tick, onMount } from 'svelte';
    import Icon from './Icon.svelte';
    import Prose from './Prose.svelte';
    import AuthEmail from './AuthEmail.svelte';
    import AuthGoogle from './AuthGoogle.svelte';
    import Popover from './Popover.svelte';
    import AuthLinks from './AuthLinks.svelte';
    import { capture_supabase_error } from '$lib/utils/capture_supabase_error';

    let { supabase, session } = $page.data;
    $: ({ supabase, session } = $page.data);

    export let hidden = false;
    export let collapsible = true;
    export let button_props = {};
    export let email = '';
    export let use_popover = false;

    let show_modal = false;
    let auth_method = null;
    let is_email_forced = false;
    let show_user_menu = false;
    let modal_title = 'Please log in with the email used for purchase';

    const dispatch = createEventDispatcher();

    $: is_email_forced = !!email;
    $: modal_title = is_email_forced
        ? `We sent a login code to ${email}, please enter it below.`
        : 'Please log in with the email used for purchase';

    export function show() {
        handle_modal_show();
    }

    export function hide() {
        handle_modal_close();
    }

    function handle_modal_close() {
        if (!collapsible) return;
        show_modal = false;
        auth_method = null;
    }

    async function handle_modal_show() {
        show_modal = true;
        auth_method = is_email_forced ? 'email' : null;
        await tick();
    }

    async function handle_sign_out() {
        show_user_menu = false;
        try {
            const { error } = await supabase.auth.signOut();
            if (error) {
                console.error('auth_sign_out_error', error);
                capture_supabase_error(error, {
                    message: 'Supabase signOut failed',
                    operation: 'auth.signOut',
                    level: is_missing_auth_session_error(error)
                        ? 'warning'
                        : 'error'
                });
            }
        } catch (error) {
            console.error('auth_sign_out_throw', error);
            capture_supabase_error(error, {
                message: 'Supabase signOut threw',
                operation: 'auth.signOut'
            });
        }

        await clear_server_auth_session();
        await clear_local_auth_session();
        location.reload();
    }

    async function clear_server_auth_session() {
        try {
            const response = await fetch('/auth/sign-out', {
                method: 'POST'
            });

            if (!response.ok) {
                console.error('server_auth_sign_out_response', {
                    status: response.status
                });
            }
        } catch (server_error) {
            console.error('server_auth_sign_out_fetch_error', server_error);
        }
    }

    async function clear_local_auth_session() {
        try {
            if (supabase.auth?.storage && supabase.auth?.storageKey) {
                await supabase.auth.storage.removeItem(
                    supabase.auth.storageKey
                );
                await supabase.auth.storage.removeItem(
                    `${supabase.auth.storageKey}-code-verifier`
                );
            }
        } catch (clear_error) {
            console.error('auth_local_sign_out_error', clear_error);
        }
    }

    function is_missing_auth_session_error(error) {
        return /session from session_id claim in jwt does not exist/i.test(
            error?.message || ''
        );
    }

    function set_auth_method(method) {
        auth_method = method;
    }

    let ctm_email = '';
    /** @type {boolean | null}*/
    let is_gmail_email = null;

    onMount(() => {
        ctm_email = localStorage.getItem('ctm_email') || '';
        is_gmail_email = ctm_email
            ? ctm_email.toLowerCase().endsWith('@gmail.com')
            : null;
    });
</script>

{#if !hidden}
    {#if session}
        {#if use_popover}
            <Popover bind:is_open={show_user_menu} position="bottom">
                <Button class="whitespace-nowrap">
                    <Icon icon="user_circle" />
                    <Icon chevron_down sm class="ml-1" />
                </Button>

                <svelte:fragment slot="content">
                    <div class="w-64 space-y-3">
                        <AuthLinks
                            sticky_headers
                            class="max-h-[70dvh] overflow-y-auto"
                            on:close={() => (show_user_menu = false)}
                        />
                        <hr class="border-border-muted" />
                        <div>
                            <Button block outline on:click={handle_sign_out}>
                                <Icon log_out />
                                Log Out
                            </Button>
                        </div>
                    </div>
                </svelte:fragment>
            </Popover>
        {:else}
            <Button
                block
                outline
                on:click={handle_sign_out}
                {...{
                    ...{ primary: false, outline: true },
                    ...button_props
                }}
            >
                <Icon log_out />
                Log Out
            </Button>
        {/if}
    {:else}
        <Button
            class="whitespace-nowrap"
            {...{
                ...{ primary: true, text: true },
                ...button_props
            }}
            on:click={handle_modal_show}
        >
            <slot name="button">
                <Icon log_in /> Log In
            </slot>
            <svelte:fragment slot="small"
                ><slot name="button_small" /></svelte:fragment
            >
        </Button>
    {/if}
{/if}

{#if show_modal}
    <Modal on:close={handle_modal_close} sm {collapsible}>
        <H2 invert class="max-w-xs mx-auto mt-8">{modal_title}</H2>

        {#if !auth_method}
            <div class="flex flex-col gap-4 max-w-xs mx-auto mt-8">
                <AuthGoogle />
                {#if is_gmail_email === true}
                    <div class="mb-2 text-center">
                        <Icon arrow_up class="mr-1" /> Last used
                    </div>
                {/if}

                <Button
                    outline
                    invert
                    xl
                    on:click={() => set_auth_method('email')}
                >
                    <Icon mail class="mr-2 !align-text-bottom" /> Sign in with Email
                </Button>
                {#if is_gmail_email === false}
                    <div class="text-center">
                        <Icon arrow_up class="mr-1" /> Last used
                    </div>
                {/if}
            </div>
        {:else if auth_method === 'email'}
            <AuthEmail
                {email}
                forced_email={is_email_forced ? email : ''}
                on:loged={(event) => dispatch('loged', event.detail)}
            />
            {#if !is_email_forced}
                <Button sm class="mt-4" on:click={() => set_auth_method(null)}
                    >Back to options</Button
                >
            {/if}
        {/if}

        <div class="border-t border-border-invert-muted mt-8 pt-4">
            <Prose invert xs class="max-w-xs mx-auto">
                <p>
                    By logging in, you agree to the <a
                        href="/terms"
                        target="_blank"
                        class="link">Terms of Service</a
                    >
                    and
                    <a href="/privacy" target="_blank" class="link"
                        >Privacy Policy</a
                    >.
                </p>
                <p>
                    If you are 13 or younger, you must have the permission of a
                    parent or guardian.
                </p>
            </Prose>
        </div>
    </Modal>
{/if}
